import { Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Logo } from "@/components/ui/logo";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserIcon, Settings, LogOut, Bell, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";

export interface HeaderProps {
  transparent?: boolean;
}

export function Header({ transparent = false }: HeaderProps) {
  // Always use white background, ignoring the transparent prop
  const { user, logoutMutation } = useAuth();
  
  const handleLogout = () => {
    logoutMutation.mutate();
  };
  
  const getInitials = (name?: string) => {
    if (!name) return "U";
    
    const names = name.split(" ");
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };
  
  return (
    <header className="sticky top-0 z-40 w-full bg-white border-b shadow-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Logo className="h-8 w-auto" />
          </div>
          
          <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-8">
            <Link href="/showcase">
              <div className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">Showcase</div>
            </Link>
            <Link href="/pricing">
              <div className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">Pricing</div>
            </Link>
            <Link href="/faq">
              <div className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">FAQ</div>
            </Link>
            
            {user ? (
              <div className="flex items-center space-x-4">
                <Button variant="ghost" size="icon" asChild>
                  <Link href="/dashboard">
                    <Bell className="h-5 w-5" />
                  </Link>
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Avatar className="h-8 w-8 cursor-pointer">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {getInitials(user.fullName || user.username)}
                      </AvatarFallback>
                    </Avatar>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem asChild>
                        <Link href="/dashboard">
                          <div className="flex items-center cursor-pointer w-full">
                            <UserIcon className="mr-2 h-4 w-4" />
                            <span>Dashboard</span>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/settings">
                          <div className="flex items-center cursor-pointer w-full">
                            <Settings className="mr-2 h-4 w-4" />
                            <span>Settings</span>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/pricing">
                          <div className="flex items-center cursor-pointer w-full">
                            <CreditCard className="mr-2 h-4 w-4" />
                            <span>Manage Subscription</span>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      disabled={logoutMutation.isPending}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{logoutMutation.isPending ? "Signing out..." : "Sign out"}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <>
                <Link href="/auth">
                  <div className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">Login</div>
                </Link>
                <Link href="/auth">
                  <Button className="ml-2">Get Started</Button>
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile menu button - only shown when not logged in */}
          {!user && (
            <div className="flex items-center sm:hidden">
              <Link href="/auth">
                <Button>Get Started</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
